
import { notification } from "antd";
import instance from "api/cmsapi";
import { useMutation, useQuery, useQueryClient } from "react-query";



/* =========================================================
   API Requests RCC Indicators
   ========================================================= */

// GET all indicators
const getAllIndicators = async () => await instance.get("rcc-indicators");

// GET indicator by ID
const getIndicatorById = async (id) => await instance.get(`rcc-indicators/${id}`);

// GET indicator by Code
const getIndicatorByCode = async (code) =>
  await instance.get(`rcc-indicators/code/${code}`);

// CREATE single indicator
const addIndicator = async (payload) =>
  await instance.post("rcc-indicators", payload);

// CREATE multiple indicators (bulk)
const addIndicatorsBulk = async (payload) =>
  await instance.post("rcc-indicators/bulk", payload);

// UPDATE indicator
const updateIndicator = async ({ id, payload }) =>
  await instance.put(`rcc-indicators/${id}`, payload);

// DELETE indicator (optional, if you implement it later)
const deleteIndicator = async (id) =>
  await instance.delete(`rcc-indicators/${id}`);


// GET ALL
export function useGetAllIndicators() {
  return useQuery(["getAllIndicators"], getAllIndicators, {
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching RCC indicators",
      }),
  });
}

// GET BY ID
export function useGetIndicatorById(id) {
  return useQuery(["getIndicatorById", id], () => getIndicatorById(id), {
    enabled: !!id,
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching indicator by ID",
      }),
  });
}

// GET BY CODE
export function useGetIndicatorByCode(code) {
  return useQuery(["getIndicatorByCode", code], () => getIndicatorByCode(code), {
    enabled: !!code,
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching indicator by code",
      }),
  });
}

// CREATE single
export function useAddIndicator() {
  const queryClient = useQueryClient();
  return useMutation(addIndicator, {
    onSuccess: () => {
      notification.success({ message: "Indicator created successfully" });
      queryClient.invalidateQueries(["getAllIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error creating indicator",
      }),
  });
}

// CREATE bulk
export function useAddIndicatorsBulk() {
  const queryClient = useQueryClient();
  return useMutation(addIndicatorsBulk, {
    onSuccess: () => {
      notification.success({ message: "Bulk indicators created successfully" });
      queryClient.invalidateQueries(["getAllIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error creating bulk indicators",
      }),
  });
}

// UPDATE
export function useUpdateIndicator() {
  const queryClient = useQueryClient();
  return useMutation(updateIndicator, {
    onSuccess: () => {
      notification.success({ message: "Indicator updated successfully" });
      queryClient.invalidateQueries(["getAllIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error updating indicator",
      }),
  });
}

// DELETE (if implemented)
export function useDeleteIndicator() {
  const queryClient = useQueryClient();
  return useMutation(deleteIndicator, {
    onSuccess: () => {
      notification.success({ message: "Indicator deleted successfully" });
      queryClient.invalidateQueries(["getAllIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error deleting indicator",
      }),
  });
}

/* =========================================================
   API Requests RCC Sub-Indicators
   ========================================================= */
// Base URL is already in instance

// GET all sub-indicators
const getAllSubIndicators = async () => await instance.get("rcc-sub-indicators");

// GET sub-indicator by ID
const getSubIndicatorById = async (id) =>
  await instance.get(`rcc-sub-indicators/${id}`);

// GET Indicators Summary
const getIndicatorsSummary = async () =>
  await instance.get('rcc-sub-indicators/summary');

// GET sub-indicator by code
const getSubIndicatorByCode = async (code) =>
  await instance.get(`rcc-sub-indicators/code/${code}`);

// GET sub-indicators by parent indicator ID
const getSubIndicatorsByIndicator = async (indicatorId) =>
  await instance.get(`rcc-sub-indicators/indicator/${indicatorId}`);

// CREATE single sub-indicator
const addSubIndicator = async (payload) =>
  await instance.post("rcc-sub-indicators", payload);

// CREATE bulk sub-indicators
const addSubIndicatorsBulk = async (payload) =>
  await instance.post("rcc-sub-indicators/bulk", payload);

// UPDATE sub-indicator
const updateSubIndicator = async ({ id, payload }) =>
  await instance.put(`rcc-sub-indicators/${id}`, payload);

// DELETE sub-indicator (optional)
const deleteSubIndicator = async (id) =>
  await instance.delete(`rcc-sub-indicators/${id}`);

// GET ALL
export function useGetAllSubIndicators() {
  return useQuery(["getAllSubIndicators"], getAllSubIndicators, {
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching RCC sub-indicators",
      }),
  });
}


//GET SUMMARY
export function useGetIndicatorsSummary() {
  return useQuery(["getAllIndicatorsSummary"], getIndicatorsSummary, {
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching Indicators Summary",
      }),
  });
}

// GET BY ID
export function useGetSubIndicatorById(id) {
  return useQuery(["getSubIndicatorById", id], () => getSubIndicatorById(id), {
    enabled: !!id,
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching sub-indicator by ID",
      }),
  });
}

// GET BY CODE
export function useGetSubIndicatorByCode(code) {
  return useQuery(["getSubIndicatorByCode", code], () => getSubIndicatorByCode(code), {
    enabled: !!code,
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error fetching sub-indicator by code",
      }),
  });
}

// GET BY PARENT INDICATOR
export function useGetSubIndicatorsByIndicator(indicatorId) {
  return useQuery(
    ["getSubIndicatorsByIndicator", indicatorId],
    () => getSubIndicatorsByIndicator(indicatorId),
    {
      enabled: !!indicatorId,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message || "Error fetching sub-indicators by indicator",
        }),
    }
  );
}

// CREATE single
export function useAddSubIndicator() {
  const queryClient = useQueryClient();
  return useMutation(addSubIndicator, {
    onSuccess: () => {
      notification.success({ message: "Sub-indicator created successfully" });
      queryClient.invalidateQueries(["getAllSubIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error creating sub-indicator",
      }),
  });
}

// CREATE bulk
export function useAddSubIndicatorsBulk() {
  const queryClient = useQueryClient();
  return useMutation(addSubIndicatorsBulk, {
    onSuccess: () => {
      notification.success({ message: "Bulk sub-indicators created successfully" });
      queryClient.invalidateQueries(["getAllSubIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error creating bulk sub-indicators",
      }),
  });
}

// UPDATE
export function useUpdateSubIndicator() {
  const queryClient = useQueryClient();
  return useMutation(updateSubIndicator, {
    onSuccess: () => {
      notification.success({ message: "Sub-indicator updated successfully" });
      queryClient.invalidateQueries(["getAllSubIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error updating sub-indicator",
      }),
  });
}

// DELETE (if implemented)
export function useDeleteSubIndicator() {
  const queryClient = useQueryClient();
  return useMutation(deleteSubIndicator, {
    onSuccess: () => {
      notification.success({ message: "Sub-indicator deleted successfully" });
      queryClient.invalidateQueries(["getAllSubIndicators"]);
    },
    onError: (error) =>
      notification.error({
        message: error?.response?.data?.message || "Error deleting sub-indicator",
      }),
  });
}

/* =========================================================
   API Requests RCC Assessment Setup
   ========================================================= */

// CREATE
const createAssessmentSetup = async (payload) =>
  await instance.post("rcc-assessment-setup", payload);

// UPDATE
const updateAssessmentSetup = async ({ id, payload }) =>
  await instance.put(`rcc-assessment-setup/${id}`, payload);

// GET ALL
const getAllAssessmentSetups = async () =>
  await instance.get("rcc-assessment-setup");

// GET BY ID
const getAssessmentSetupById = async (id) =>
  await instance.get(`rcc-assessment-setup/${id}`);

// GET BY PERIOD
const getAssessmentByPeriod = async ({ regionCode, year, quarter }) =>
  await instance.get(
    `rcc-assessment-setup/period?regionCode=${regionCode}&year=${year}&quarter=${quarter}`
  );

// GET DETAILS
const getAssessmentDetails = async ({ regionCode, year, quarter }) =>
  await instance.get(
    `rcc-assessment-setup/details?regionCode=${regionCode}&year=${year}&quarter=${quarter}`
  );
  

  /* =========================================================
   React Query Hooks RCC Assessment Setup
   ========================================================= */

// GET ALL
export function useGetAllAssessmentSetups() {
  return useQuery(["getAllAssessmentSetups"], getAllAssessmentSetups, {
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error fetching RCC assessment setups",
      }),
  });
}

// GET BY ID
export function useGetAssessmentSetupById(id) {
  return useQuery(
    ["getAssessmentSetupById", id],
    () => getAssessmentSetupById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessment setup",
        }),
    }
  );
}

// GET BY PERIOD
export function useGetAssessmentByPeriod(params) {
  return useQuery(
    ["getAssessmentByPeriod", params],
    () => getAssessmentByPeriod(params),
    {
      enabled: !!params?.regionCode && !!params?.year && !!params?.quarter,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessment by period",
        }),
    }
  );
}

// GET DETAILS
export function useGetAssessmentDetails(params) {
  return useQuery(
    ["getAssessmentDetails", params],
    () => getAssessmentDetails(params),
    {
      enabled: !!params?.regionCode && !!params?.year && !!params?.quarter,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching assessment details",
        }),
    }
  );
}

// CREATE
export function useCreateAssessmentSetup() {
  const queryClient = useQueryClient();

  return useMutation(createAssessmentSetup, {
    onSuccess: () => {
      notification.success({
        message: "Assessment setup created successfully",
      });

      queryClient.invalidateQueries(["getAllAssessmentSetups"]);
      queryClient.invalidateQueries(["getAssessmentByPeriod"]);
      queryClient.invalidateQueries(["getAssessmentDetails"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating assessment setup",
      }),
  });
}

// UPDATE
export function useUpdateAssessmentSetup() {
  const queryClient = useQueryClient();

  return useMutation(updateAssessmentSetup, {
    onSuccess: () => {
      notification.success({
        message: "Assessment setup updated successfully",
      });

      queryClient.invalidateQueries(["getAllAssessmentSetups"]);
      queryClient.invalidateQueries(["getAssessmentByPeriod"]);
      queryClient.invalidateQueries(["getAssessmentDetails"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating assessment setup",
      }),
  });
}

// Add these to your existing setup.service.js file

// // GET assessment setup by period
// const getAssessmentSetupByPeriod = async ({ regionCode, year, quarter }) => {
//   return await instance.get(`rcc-assessment-setup/period?regionCode=${regionCode}&year=${year}&quarter=${quarter}`);
// };

// // GET assessment by district and setup
// const getAssessmentByDistrict = async ({ districtCode, assessmentSetupId }) => {
//   return await instance.get(`rcc-assessments/district/${districtCode}/${assessmentSetupId}`);
// };

// // CREATE assessment
// const createAssessment = async (payload) => {
//   return await instance.post("rcc-assessments", payload);
// };

// // UPDATE assessment
// const updateAssessment = async ({ id, payload }) => {
//   return await instance.put(`rcc-assessments/${id}`, payload);
// };

// // React Query hooks
// export function useGetAssessmentSetupByPeriod(params) {
//   return useQuery(
//     ["getAssessmentSetupByPeriod", params],
//     () => getAssessmentSetupByPeriod(params),
//     {
//       enabled: !!params?.regionCode && !!params?.year && !!params?.quarter,
//       onError: (error) =>
//         notification.error({
//           message: error?.response?.data?.message || "Error fetching assessment setup",
//         }),
//     }
//   );
// }

// export function useGetAssessmentByDistrict(params) {
//   return useQuery(
//     ["getAssessmentByDistrict", params],
//     () => getAssessmentByDistrict(params),
//     {
//       enabled: !!params?.districtCode && !!params?.assessmentSetupId,
//       onError: (error) =>
//         notification.error({
//           message: error?.response?.data?.message || "Error fetching assessment data",
//         }),
//     }
//   );
// }

// export function useCreateAssessment() {
//   const queryClient = useQueryClient();
//   return useMutation(createAssessment, {
//     onSuccess: () => {
//       notification.success({ message: "Assessment saved successfully" });
//       queryClient.invalidateQueries(["getAssessmentByDistrict"]);
//     },
//     onError: (error) =>
//       notification.error({
//         message: error?.response?.data?.message || "Error saving assessment",
//       }),
//   });
// }

// export function useUpdateAssessment() {
//   const queryClient = useQueryClient();
//   return useMutation(updateAssessment, {
//     onSuccess: () => {
//       notification.success({ message: "Assessment updated successfully" });
//       queryClient.invalidateQueries(["getAssessmentByDistrict"]);
//     },
//     onError: (error) =>
//       notification.error({
//         message: error?.response?.data?.message || "Error updating assessment",
//       }),
//   });
// }


// ==========================
// API FUNCTIONS
// ==========================
 
// CREATE
const createRCCAssessment = async (payload) => {
  const { data } = await instance.post(`rcc-assessments`, payload);
  return data;
};
 
// UPDATE
const updateRCCAssessment = async ({ id, payload }) => {
  const { data } = await instance.put(
    `rcc-assessments/${id}`,
    payload
  );
  return data;
};
 
// GET ALL
const getAllRCCAssessments = async () => {
  const { data } = await instance.get(`rcc-assessments`);
  return data;
};
 
// GET BY ID
const getRCCAssessmentById = async (id) => {
  const { data } = await instance.get(
    `rcc-assessments/${id}`
  );
  return data;
};
 
// GET BY DISTRICT + SETUP
const getRCCAssessmentByDistrict = async (params) => {
  const { districtCode, assessmentSetupId } = params;
 
  const { data } = await instance.get(
    `rcc-assessments/district`,
    {
      params: {
        districtCode,
        assessmentSetupId,
      },
    }
  );
 
  return data;
};
 
// GET BY REGION + YEAR + QUARTER
const getRCCAssessmentByRegion = async (params) => {
  const { regionCode, year, quarter } = params;
 
  const { data } = await instance.get(
    `rcc-assessments/region/${regionCode}/${year}/${quarter}`
  );
 
  return data;
};
 
 
// ==========================
// REACT QUERY HOOKS
// ==========================
 
// GET BY REGION
export function useGetRCCAssessmentByRegion(params) {
  return useQuery(
    ["getRCCAssessmentByRegion", params],
    () => getRCCAssessmentByRegion(params),
    {
      enabled: !!params?.regionCode && !!params?.year && !!params?.quarter,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching RCC assessments by region",
        }),
    }
  );
}
 
// GET BY DISTRICT
export function useGetRCCAssessmentByDistrict(params) {
  return useQuery(
    ["getRCCAssessmentByDistrict", params],
    () => getRCCAssessmentByDistrict(params),
    {
      enabled: !!params?.districtCode && !!params?.assessmentSetupId,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching RCC assessment",
        }),
    }
  );
}
 
// GET ALL
export function useGetAllRCCAssessments() {
  return useQuery(
    ["getAllRCCAssessments"],
    getAllRCCAssessments,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching all RCC assessments",
        }),
    }
  );
}
 
// GET BY ID
export function useGetRCCAssessmentById(id) {
  return useQuery(
    ["getRCCAssessmentById", id],
    () => getRCCAssessmentById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching RCC assessment details",
        }),
    }
  );
}
 
// CREATE
export function useCreateRCCAssessment() {
  const queryClient = useQueryClient();
 
  return useMutation(createRCCAssessment, {
    onSuccess: () => {
      notification.success({
        message: "RCC Assessment created successfully",
      });
 
      queryClient.invalidateQueries(["getAllRCCAssessments"]);
      queryClient.invalidateQueries(["getRCCAssessmentByRegion"]);
      queryClient.invalidateQueries(["getRCCAssessmentByDistrict"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating RCC assessment",
      }),
  });
}
 
// UPDATE
export function useUpdateRCCAssessment() {
  const queryClient = useQueryClient();
 
  return useMutation(updateRCCAssessment, {
    onSuccess: () => {
      notification.success({
        message: "RCC Assessment updated successfully",
      });
 
      queryClient.invalidateQueries(["getAllRCCAssessments"]);
      queryClient.invalidateQueries(["getRCCAssessmentByRegion"]);
      queryClient.invalidateQueries(["getRCCAssessmentByDistrict"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating RCC assessment",
      }),
  });
}

